import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddProductPanelService } from '../../services/add-product-panel.service';
import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OrdersService } from '../../api/orders.service';
import { ProductModel, ProductsService } from '../../api/products.service';
import { GT2PageAbstract } from '../../content/abstract/GT2PageAbstract';
import { ItemUrlFinder } from '../../utils/item-url-finder';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { CreateProductComponent } from '../dialogs/create-product/create-product.component';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';
import { AuthenticationService } from '../../api/auth-service.service';
import { ChoiceComboSelectDialogComponent } from '../../content/products/choice-combo-select-dialog/choice-combo-select-dialog.component';
import { ModulesService } from '../../services/modules.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Gt2PrimengModule } from '../../modules/gt2-primeng.module';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgPipesModule } from 'ngx-pipes';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FuseDirectivesModule } from '../../../../@fuse/directives/directives';
import { FusePipesModule } from '../../../../@fuse/pipes/pipes.module';
import {
  copyArrayItem,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MobileLayoutService } from '../../services/mobile-layout.service';
import { TagsService } from '../tags/tags.service';
import { ProductCategoryService } from '../../api/product-category.service';

@Component({
  selector: 'app-order-product-dnd',
  templateUrl: './order-product-dnd.component.html',
  styleUrls: ['./order-product-dnd.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FuseDirectivesModule,
    DragDropModule,
    FusePipesModule,
    TranslateModule,
    MatInputModule,
    MatTooltipModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    Gt2PrimengModule,
    MatBadgeModule,
    MatIconModule,
    ScrollingModule,
    NgPipesModule,
  ],
})
export class OrderProductDndComponent
  extends GT2PageAbstract
  implements OnInit, AfterViewInit, OnDestroy
{
  products?: ProductModel[];
  combo: any[] = [];
  isLoading: boolean = false;
  isEmpty: boolean = true;
  creationMode: boolean = false;
  applyCall: boolean = true;
  @ViewChild('filter') filter?: ElementRef;
  @ViewChild('productList') productList: ElementRef | any;
  filterValue: string = '';
  newDialogRef: any;
  fromEventSubscription?: Subscription;
  orderUUID: string | any;
  defaultQty: number = 1;
  // Mode 'selection' (upsale) : aucune API au save, on retourne la liste au caller.
  isSelectionMode: boolean = false;
  customTitle: string | null = null;
  // Affiche « prix original » + champ « nouveau prix » sur les items de droite.
  showPriceOverride: boolean = false;
  groupMeta: { name: string; choice_required_no_skip?: boolean } | null = null;
  groupName: string = '';
  groupChoiceRequired: boolean = false;
  showChoiceRequiredCheckbox: boolean = false;
  choiceComboDialogRef: any;
  isSmallScreen: boolean = false;
  isMediumScreen: boolean = false;
  readonly mobileLayout = inject(MobileLayoutService);

  // Filtres additionnels (catégorie de produit + tags) — mêmes sources que la
  // page liste des produits.
  productCategories: any[] = [];
  selectedCategoryUuid: string | null = null;
  availableTags: any[] = [];
  selectedTagUuids: string[] = [];

  /** Affichage uniquement : message « aucun résultat » à partir de ce nombre de caractères (trim). L’API reste à 2 via le debounce. */
  readonly minCharsForEmptyMessage = 3;

  constructor(
    private panelService: AddProductPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    public productsService: ProductsService,
    public ordersService: OrdersService,
    public authService: AuthenticationService,
    private dialog: MatDialog,
    public router: Router,
    private toastr: ToastrService,
    private logger: NGXLogger,
    public modulesService: ModulesService,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private tagsService: TagsService,
    private productCategoryService: ProductCategoryService,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data = this.panelService.getData();
    if (data?.orderUUID) {
      this.orderUUID = data.orderUUID;
    }
    if (data?.guest_count) {
      this.defaultQty = data.guest_count;
    }
    this.isSelectionMode = data?.mode === 'selection';
    this.customTitle = data?.title ?? null;
    this.showPriceOverride = !!data?.priceOverride;
    if (data?.initialSelection) {
      // Copie superficielle : le panneau mute qty sur les items.
      this.combo = data.initialSelection.map((item: any) => ({ ...item }));
    }
    if (data?.groupMeta) {
      this.groupMeta = data.groupMeta;
      this.groupName = data.groupMeta.name ?? '';
      this.showChoiceRequiredCheckbox =
        data.groupMeta.choice_required_no_skip !== undefined;
      this.groupChoiceRequired = !!data.groupMeta.choice_required_no_skip;
    }
  }

  get saveDisabled(): boolean {
    return (
      this.isLoading || (!!this.groupMeta && this.groupName.trim().length === 0)
    );
  }

  ngOnInit(): void {
    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe({
      next: (result) => {
        this.isSmallScreen = result.matches;
      },
    });
    this.breakpointObserver.observe(['(max-width: 1400px)']).subscribe({
      next: (result) => {
        this.isMediumScreen = result.matches;
      },
    });
    // this.loadProductList();

    this.productCategoryService.getCategoriesList().subscribe({
      next: (response: any) => {
        this.productCategories = response?.data ?? [];
      },
      error: (error: any) => {
        this.logger.error(
          'OrderProductDndComponent.getCategoriesList() -> ERROR: ' +
            JSON.stringify(error),
        );
      },
    });

    // Mêmes familles de tags que le filtre de la page liste des produits,
    // plus les tags sur mesure créés sur la page produit.
    const tagFamilies = [
      TagsService.TAG_FAMILY_PRODUCT_OPEN,
      TagsService.TAG_FAMILY_PRODUCT_MOMENT,
      TagsService.TAG_FAMILY_PRODUCT_SEASON,
      TagsService.TAG_FAMILY_PRODUCT_FEATURE,
      TagsService.TAG_FAMILY_PRODUCT_CUSTOM,
    ];
    for (const family of tagFamilies) {
      this.tagsService.getTagsByFamily(family).subscribe({
        next: (response: any) => {
          const tags = response.data || response || [];
          this.availableTags = [
            ...this.availableTags,
            ...tags.filter(
              (t: any) => !this.availableTags.some((at) => at.uuid === t.uuid),
            ),
          ];
        },
      });
    }
  }

  ngAfterViewInit(): void {
    this.fromEventSubscription = fromEvent(this.filter?.nativeElement, 'keyup')
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => {
          // //this.logger.info("OrderProductDndComponent.onFilterChange() -> " + this.productList);
          // //this.logger.info("OrderProductDndComponent.onFilterChange() -> " + this.filterValue);
          if (this.canSearch) {
            this.loadProductList();
            this.cdr.detectChanges();
          } else {
            this.products = [];
            this.isEmpty = true;
          }
          /*
                              if (this.filterValue.length >= 3 || this.filterValue === "")
                              {
                                this.loadProductList();
                              }
                    */
          // this.productList.nativeElement.scrollTop = 0;
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {}

  // Une recherche est possible dès qu'il y a un mot-clé (≥2 caractères, comme
  // le debounce) OU un filtre catégorie/tags actif — les filtres seuls
  // permettent de naviguer par catégorie sans taper de texte.
  get hasActiveFilters(): boolean {
    return !!this.selectedCategoryUuid || this.selectedTagUuids.length > 0;
  }

  get canSearch(): boolean {
    return (this.filterValue?.trim().length ?? 0) >= 2 || this.hasActiveFilters;
  }

  public onProductFilterChange(): void {
    if (this.productList?.nativeElement) {
      this.productList.nativeElement.scrollTop = 0;
    }
    if (this.canSearch) {
      this.loadProductList();
    } else {
      this.products = [];
      this.isEmpty = true;
    }
  }

  public loadProductList(): void {
    this.isLoading = true;
    this.isEmpty = false;
    this.productsService
      .getProducts(
        this.filterValue,
        this.selectedCategoryUuid,
        this.selectedTagUuids,
      )
      .subscribe({
      next: (response: any) => {
        //this.logger.info("OrderProductDndComponent.getProducts() -> SUCCESS>>: " + JSON.stringify(response));
        this.isLoading = false;
        if (response.data.length > 0) {
          this.products = response.data;
          this.isEmpty = false;
          this.productList.nativeElement.scrollTop = 0;
        } else {
          this.isEmpty = true;
          this.products = [];
        }
      },
      error: (error: any) => {
        this.logger.error(
          'OrderProductDndComponent.getProducts() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(error, this.dialog, null, null, this.router);
        this.isLoading = false;
        this.isEmpty = true;
      },
    });
  }

  public onFilterChange() {
    // //this.logger.info("OrderProductDndComponent.onFilterChange()");
    this.productList.nativeElement.scrollTop = 0;
  }

  public onSaveProducts(): void {
    // Prevent double-click by checking if already loading
    if (this.saveDisabled) {
      return;
    }

    if (this.isSelectionMode) {
      this.panelService.emitResult({
        products: this.combo,
        name: this.groupMeta ? this.groupName.trim() : undefined,
        choice_required_no_skip: this.showChoiceRequiredCheckbox
          ? this.groupChoiceRequired
          : undefined,
      });
      return;
    }

    // //this.logger.info("OrderProductDndComponent.onSaveProducts() -> products: " + JSON.stringify(this.combo));
    this.isLoading = true;
    this.ordersService
      .addProduct(this.orderUUID, { products: this.combo })
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.panelService.emitResult(response.data);
        },
        error: (error: any) => {
          this.logger.error(
            'OrderProductDndComponent.getProducts() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.isLoading = false;
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public onDrop(event: any): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(this.combo, event.previousIndex, event.currentIndex);
    } else {
      //this.logger.info(event.source);
      // //this.logger.info(event.source.className.indexOf("combo-list"));
      if (event.item.data == undefined) {
        return;
      }
      //this.logger.info("OrderProductDndComponent.onDrop() -> item: " + JSON.stringify(event.item.data));

      // if (event.source.className.indexOf('combo-list') !== -1) {
      //   this.onProductChildDrop(event);
      //   return;
      // }

      // event.item.data.qty = 1;
      //this.logger.info("DROP: event.item.data.qty: " + event.item.data.qty);
      const items: any[] = this.combo.filter((item) => {
        return item.uuid === event.item.data.uuid;
      });
      // //this.logger.info("OrderProductDndComponent.onDrop() -> items: " + items.length);

      // If this product was already in the list, we add to qty
      if (items && items.length > 0) {
        event.item.data.qty = event.item.data.qty + 1;
      } else {
        event.item.data.qty = this.defaultQty;
        copyArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex,
        );
      }
      //
      if (
        !this.isSelectionMode &&
        this.modulesService.modulesAreSet &&
        this.modulesService.hasComboDynamicModule() &&
        event.item.data.is_choice_product_combo
      ) {
        //this.logger.info("DROP: IS is_choice_product_combo! CHOOSE/SELEC PRODUCT FROM CHILDREN");
        this.presentDynamicComboChoiceSelection(event.item.data);
        return;
      }
    }
  }

  public presentDynamicComboChoiceSelection(product: ProductModel): void {
    //
    // //this.logger.info("OrderProductDndComponent.onProductChildDrop() -> " + JSON.stringify(product));
    let choicesProduct = [];
    // let regularProduct = [];
    for (let i = 0; i < product.childs.length; i++) {
      let p = product.childs[i];
      //this.logger.info("OrderProductDndComponent.presentDynamicComboChoiceSelection() -> " + p.name_internal + " " + p.is_combo);
      //this.logger.info("OrderProductDndComponent.presentDynamicComboChoiceSelection() ->  p.qty: " + p.qty);
      if (p.is_combo) {
        choicesProduct.push(p);
        // this.combo.push(p.childs[0]);
      } else {
        // regularProduct.push(p);
      }
    }
    // Aucun enfant de type combo = aucun choix à faire : tous les composants
    // sont fixes. On n'ouvre pas le popup (il serait vide) et on soumet
    // directement le combo dynamique pour qu'il soit transformé en combo.
    if (choicesProduct.length === 0) {
      this.submitDynamicComboWithoutChoices(product);
      return;
    }
    //
    this.choiceComboDialogRef = this.dialog.open(
      ChoiceComboSelectDialogComponent,
      {
        width: '600px',
        minWidth: 300,
        // minWith: "300px",
        disableClose: false,
        panelClass: 'custom-dialog-container',
        data: {
          choicesProduct: choicesProduct,
          forProduct: product.name_internal,
          forProductUUID: product.uuid,
        },
      },
    );

    this.choiceComboDialogRef.afterClosed().subscribe({
      next: (result: any) => {
        if (result) {
          //this.logger.info("OrderProductDndComponent.presentDynamicComboChoiceSelection() -> choices: " + JSON.stringify(result));
          result.data.qty = this.defaultQty;
          this.combo.push(result.data);
          // for ( const p in result.data ) {
          //     //this.logger.info("OrderProductDndComponent.presentDynamicComboChoiceSelection() -> product: " + JSON.stringify(result.data[p]));
          //     result.data[p].qty = this.defaultQty;
          //     this.combo.push(result.data[p]);
          // }
          // for (let i = 0; i < regularProduct.length; ++i) {
          //     this.combo.push(regularProduct[i]);
          // }
        }
        //
        // this.combo.sort(function(a, b){
        //     return product.childs.indexOf(a) - product.childs.indexOf(b);
        // });
        //
        this.choiceComboDialogRef = null;
      },
    });
    //
    this.combo = this.combo.filter((productItem) => {
      //this.logger.info(productItem);
      return productItem.uuid !== product.uuid;
    });
  }

  /**
   * Combo dynamique sans choix (aucun enfant `is_combo`) : on transforme
   * directement le combo dynamique en combo côté serveur, sans afficher de
   * popup, puis on remplace le produit combo dynamique par le combo résolu.
   */
  public submitDynamicComboWithoutChoices(product: ProductModel): void {
    this.productsService.createChoiceComboFromChoice({}, product.uuid).subscribe({
      next: (response: any) => {
        response.data.qty = this.defaultQty;
        this.combo.push(response.data);
        //
        this.combo = this.combo.filter((productItem) => {
          return productItem.uuid !== product.uuid;
        });
      },
      error: (error: any) => {
        this.logger.error(
          'OrderProductDndComponent.submitDynamicComboWithoutChoices() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public onProductChildDrop(event: any) {
    // //this.logger.info("OrderProductDndComponent.onProductChildDrop() -> " + JSON.stringify(event.item.data));
    // //this.logger.info("OrderProductDndComponent.onProductChildDrop() -> " + JSON.stringify(this.product.childs));
    // //this.logger.info(event);
  }

  public onRemoveItem(item: any, index: number) {
    // //this.logger.info("OrderProductDndComponent.onRemoveItem() -> index: " + index);
    item.qty = 0;
    for (let i = 0; i < this.combo.length; ++i) {
      if (this.combo[i] === item) {
        this.combo.splice(i, 1);
        break;
      }
    }
  }

  public onItemQtyChange(event: any, item: any): void {
    // Mutate in place: keeping the same object identity prevents the @for
    // (track comb) from destroying/recreating the row, which would make the
    // input lose focus on every keystroke.
    item.qty = parseInt(event) || event;
  }

  // Prix original (lecture seule) du produit-réponse. Provenance selon la
  // source : depuis le catalogue on a `price_label` déjà formaté ; à l'édition
  // d'une sélection existante, le caller transmet `amount` (en cents) ou un
  // `original_price_label` prêt à l'affichage.
  public getOriginalPriceLabel(item: any): string {
    // `original_amount` (cents) = prix catalogue livré par l'API vente
    // incitative. À défaut, on retombe sur les libellés/prix du catalogue.
    if (typeof item?.original_amount === 'number') {
      return (item.original_amount / 100).toFixed(2) + ' $';
    }
    if (item?.original_price_label) {
      return item.original_price_label;
    }
    if (item?.price_label) {
      return item.price_label;
    }
    if (typeof item?.price === 'number') {
      return item.price.toFixed(2) + ' $';
    }
    return '—';
  }

  // Nouveau prix (override) saisi en dollars. Muter in place (même raison que
  // onItemQtyChange) pour ne pas perdre le focus à chaque frappe.
  public onOverridePriceChange(value: any, item: any): void {
    if (value === null || value === undefined || value === '') {
      item.override_price = null;
      return;
    }
    const parsed = parseFloat(String(value).replace(',', '.'));
    item.override_price = isNaN(parsed) ? null : parsed;
  }

  public onAddItem(item: any, doAddItemInModel: boolean = false) {
    const items: any[] = this.combo.filter((filterItem) => {
      // //this.logger.info(filterItem.uuid === item.uuid);
      return filterItem.uuid === item.uuid;
    });

    if (items.length === 0) {
      // item.qty = 1;
      item.qty = this.defaultQty;
      this.combo.push(item);
    } else {
      items[0].qty++;
    }
    // //this.logger.info("OrderProductDndComponent.onAddItem() -> items: " + items.length);
    //
    if (
      !this.isSelectionMode &&
      this.modulesService.modulesAreSet &&
      this.modulesService.hasComboDynamicModule() &&
      item.is_choice_product_combo
    ) {
      //this.logger.info("onAddItem: IS is_choice_product_combo! CHOOSE/SELEC PRODUCT FROM CHILDREN");
      this.presentDynamicComboChoiceSelection(item);
      return;
    }
  }

  public onLessItem(item: any, i: number): void {
    item.qty -= 1;

    if (item.qty === 0) {
      this.onRemoveItem(item, i);
    }
  }

  public onRemove(event: any) {
    // //this.logger.info("OrderProductDndComponent.onRemove() -> combo: " + this.combo.length);
    // //this.logger.info("OrderProductDndComponent.onRemove() -> combo: " + event);

    if (event.value.qty > 1) {
      event.value.qty -= 1;
      this.combo.push(event.value);
    }
  }

  public clearSearch(): void {
    this.filterValue = '';
    // Des filtres catégorie/tags encore actifs continuent de piloter la liste.
    if (this.hasActiveFilters) {
      this.loadProductList();
    } else {
      this.products = [];
      this.isEmpty = true;
    }
  }

  public onCreateProduct(): void {
    // //this.logger.info("OrderProductDndComponent.onCreateProduct()");
    this.newDialogRef = this.dialog.open(CreateProductComponent, {
      width: '340px',
      disableClose: false,
      panelClass: 'custom-container',
      data: {
        autoRedirect: false,
        ...(this.orderUUID ? { uuid_order: this.orderUUID } : {}),
      },
    });

    this.newDialogRef.afterClosed().subscribe({
      next: (result: any) => {
        if (result) {
          // //this.logger.info("OrderProductDndComponent.onCreateMaterial() -> SUCCESS: " + JSON.stringify(result));
          // this.toastr.success("", this.translate.instant("GENERIC.API_CALL_SUCCESS.CREATE_SUCCESS"));
          result.qty = 1;
          this.combo.push(result);
          if (this.canSearch) {
            this.loadProductList();
            this.cdr.detectChanges();
          }
        }
        this.newDialogRef = null;
      },
    });
  }

  public onOpenInNewTab(item: any): void {
    const prefix = localStorage.getItem('PREFIX') || '';
    window.open(prefix + ItemUrlFinder.getItemURL(item), '_blank');
  }

  public close(): void {
    this.panelService.emitResult(false);
  }

  get showSearchProductHint(): boolean {
    return (
      this.isEmpty &&
      !this.isLoading &&
      !this.hasActiveFilters &&
      (this.filterValue?.trim().length ?? 0) < this.minCharsForEmptyMessage
    );
  }

  get showNoSearchResults(): boolean {
    return (
      this.isEmpty &&
      !this.isLoading &&
      (this.hasActiveFilters ||
        (this.filterValue?.trim().length ?? 0) >= this.minCharsForEmptyMessage)
    );
  }
}
